/* eslint-disable react/button-has-type */
import React, { useEffect, useReducer } from 'react'
import get from 'lodash/get'
import dynamic from 'next/dynamic'
import Loading from 'Components/loading'
import formatTime from 'lib/format-time'
import {
  getArticleDetail,
  IArticle,
  TArticleMotorMaterialCarInfo,
} from '../../services/index'
import FollowBtn from './follow-btn'
import Avatar from 'Components/author/avatar'
import canUseWebp from 'lib/can-use-webp'
import {
  ReportButton,
  InteractVideo,
} from 'Components/new/community-card/interact'
import Share from 'Components/new/community-card/share'
import xss from 'xss'
import { sendTeaCommEvent } from 'lib/tea-analyze'
import ARTICLE_CONTENT_TYPE from 'Constants/articleType'
import { emoji } from 'Constants/emoji'
import RelationSeries, { addRelationSeriesListeners } from './relation-series'
import relationSeriesEvent from './relation-series-tea'
import ReactDOMServer from 'react-dom/server'
import * as ZT from 'Constants/zt'
import * as LS from 'Constants/link-source'
import { useCityName } from 'Components/hooks'
import getUserInfo from 'lib/user-info'
import ReactDOM from 'react-dom'
import getPageEnterId from 'lib/page-enter'
import { useRouter } from 'next/router'
const Video = dynamic(() => import('Components/player'), {
  ssr: false,
  loading: () => <Loading />,
})

interface IProps {
  article: IArticle
  isVideo: boolean
  group_id: string
  digg_count: number
  collect_curStatus: boolean
  digg_curStatus: boolean
  collectUpdate(): void
  diggUpdate(): void
}
// 图片关联车系过滤，相同车系仅保留首次与末次出现的关联信息
function getUniqueRelatedSeries(carInfo: IArticle['motor_material_car_info']) {
  if (!carInfo) return null
  const sequenceMap = new Map<number, string[]>()
  const map = new Map<string, TArticleMotorMaterialCarInfo>()
  const list = Object.keys(carInfo).map((key) => ({ key, info: carInfo[key] }))
  list.sort((prev, next) => (next.info.index || 0) - (prev.info.index || 0))
  list.forEach(({ key, info }) => {
    const seriesId = info.series_id || 0
    const sequence = sequenceMap.get(seriesId) || []
    if (sequence.length === 2) {
      const last = sequence.pop()
      map.delete(last as string)
    }
    sequence.push(key)
    sequenceMap.set(seriesId, sequence)
    map.set(key, info)
  })
  return map
}
interface IGetRelatedSeriesComponentParams {
  carInfo: TArticleMotorMaterialCarInfo
  key?: string
  clue: Record<'zt' | 'ls', string>
  onClick?: (
    event: React.MouseEvent,
    type: string,
    params: Record<string, any>
  ) => void
}
function getRelatedSeriesComponent({
  carInfo,
  key,
  onClick,
  clue,
}: IGetRelatedSeriesComponentParams) {
  return (
    <RelationSeries
      uriKey={key}
      seriesId={carInfo.series_id}
      seriesName={carInfo.series_name}
      brandId={carInfo.brand_id}
      price={carInfo.dealer_low_price}
      cutPrice={carInfo.price_reduction}
      hasPrice={carInfo.has_dealer_price}
      hasCutPrice={
        !!carInfo.price_reduction && carInfo.price_reduction !== '暂无'
      }
      hasImage={!!carInfo.series_pic_count}
      hasTest={carInfo.has_eval}
      carIds={carInfo.online_car_ids}
      score={carInfo.dcd_score}
      communityCount={carInfo.car_friend_count_en}
      hasUsedCar={carInfo.has_sh_info}
      usedCarCount={carInfo.sku_count}
      usedCarPrice={carInfo.sh_min_price}
      zt={clue.zt}
      linkSource={clue.ls}
      onClick={onClick}
    />
  )
}
// 插入图片关联车系
function injectRelatedSeries(
  content: string,
  relatedCarInfo: IArticle['motor_material_car_info'],
  clue: Record<'zt' | 'ls', string>
) {
  let injectedContent = content
  const relatedSeries = getUniqueRelatedSeries(relatedCarInfo)
  if (!relatedSeries?.size || !content) return content
  relatedSeries.forEach((carInfo, key) => {
    const relatedSeriesComponent = getRelatedSeriesComponent({
      carInfo,
      key,
      clue,
    })
    const injectStr = ReactDOMServer.renderToString(relatedSeriesComponent)
    const result = new RegExp(`(${key})[^>]+>`).exec(injectedContent)
    if (!result) return
    injectedContent = injectedContent.replace(
      new RegExp(`(${key}[^>]+>)`),
      `$1${injectStr}`
    )
  })
  return injectedContent
}

// 刷新图片关联车系
function refreshRelationSeries(
  article: IArticle,
  clue: Record<'zt' | 'ls', string>
) {
  const relatedSeries = getUniqueRelatedSeries(article.motor_material_car_info)
  if (!relatedSeries?.size || !article.content) return
  const onClick = (
    e: React.MouseEvent,
    type: string,
    params: Record<string, any>
  ) => relationSeriesEvent(type, params)
  relatedSeries?.forEach((carInfo, key) => {
    const container = document.querySelector(`[data-image="${key}"]`)
    if (!container) return
    ReactDOM.render(
      getRelatedSeriesComponent({ carInfo, onClick, clue }),
      container
    )
  })
}
function handleContent(
  content: string,
  relatedCarInfo: IArticle['motor_material_car_info'],
  clue: Record<'zt' | 'ls', string>
): string {
  const afterEmojiContent = emoji(content.replace(/\{!--.*?--\}/g, ''))
  return injectRelatedSeries(afterEmojiContent, relatedCarInfo, clue)
}

export function replace(
  input: string,
  re: RegExp,
  replacer: (match: RegExpExecArray) => string
) {
  let match: RegExpExecArray | null
  let remaining = input
  let rewritten = ''
  while ((match = re.exec(remaining))) {
    rewritten += remaining.slice(0, match.index)
    rewritten += replacer(match)
    remaining = remaining.slice(match.index + match[0].length)
  }
  rewritten += remaining
  return rewritten
}

function replaceOuterHTML(content: string): string {
  return replace(
    content,
    /<html><body>(.+?)<\/body><\/html>/,
    ([, content]) => content
  )
}

const HTML_BR_REG = /^<br>$/
// 处理换行符
const processBr = (p: HTMLParagraphElement) => {
  // 针对编辑器最新的换行符<p><br></p>进行处理
  if (HTML_BR_REG.test(p.innerHTML)) {
    p.classList.add('syl-page-br')
    // if (!p.nextElementSibling) { // 行尾BR不处理
    //   return
    // }
    // if (!p.previousElementSibling) { // 行首syl-page-br直接移除
    if (p.parentNode) {
      p.parentNode.removeChild(p)
    }
    // return
    // }
    // 处理段落中间的BR
    // const shoudleHideBr = (
    //   // 段落和图片之间的换行隐藏
    //   (p.nextElementSibling.classList.contains('pgc-img') && p.previousElementSibling.tagName === 'P') ||
    //   // 连续多个换行隐藏掉后面的
    //   p.previousElementSibling.classList.contains('syl-page-br') ||
    //   // 图片后面的换行符隐藏
    //   p.previousElementSibling.classList.contains('pgc-img')
    // )
    // if (shoudleHideBr) {
    //   p.classList.add('syl-page-br-hide')
    // }
  }
  /**
   * 两个br元素之间若包含textNode，不认为是连续换行。Migrate from tt_article_detail
   */
  const originBrs = Array.from(p.querySelectorAll('br'))
  originBrs.forEach((br) => {
    const preNode = br.previousSibling
    if (preNode && preNode.nodeType === Node.TEXT_NODE) {
      br.classList.add('sysbr')
    }
  })
}

export default function Content({
  article,
  isVideo,
  group_id,
  digg_count,
  collect_curStatus,
  digg_curStatus,
  collectUpdate,
  diggUpdate,
}: IProps) {
  const router = useRouter()
  const clueMap: Record<string, Record<'zt' | 'ls', string>> = {
    page_pgc_article_detail: {
      zt: ZT.ZT_0015,
      ls: LS.LS_0007,
    },
    page_feed_article_mix: {
      zt: ZT.ZT_0045,
      ls: LS.LS_0006,
    },
  }
  const currentClue = clueMap[getPageEnterId(router.asPath).page_id]
  const cityName = useCityName()
  const allowReloadRef = React.useRef(false)
  React.useEffect(() => {
    if (!cityName) return
    if (!allowReloadRef.current) {
      allowReloadRef.current = true
      return
    }
    ;(async () => {
      try {
        const userData = getUserInfo()
        const articleData = await getArticleDetail({
          group_id,
          user_id: userData?.user_id,
          city_name: cityName,
        })
        refreshRelationSeries(articleData.article as IArticle, currentClue)
      } catch {
        // no-op
      }
    })()
  }, [cityName])
  /* 渲染标题 */
  function renderHeader() {
    return (
      <div className="details">
        <h1 className="title">{article?.title}</h1>
        {article?.media_user && (
          <div
            data-log-click={JSON.stringify({
              event: 'clk_event',
              params: {
                event_id: 'content_user_profile',
                to_user_id: `${article?.media_user?.id}`,
              },
            })}
            className="info"
          >
            <Avatar
              size={24}
              style={{ marginRight: 8 }}
              userId={article?.media_user?.id}
              avatar={article?.media_user?.avatar_url}
              username={article?.media_user?.screen_name}
            />
            <a
              href={`/user/${article?.media_user?.id}`}
              target={'_blank'}
              className="source g-active-link-text"
            >
              {article?.media_user?.screen_name}
            </a>
            <span className="time">
              {formatTime(article?.publish_time * 1000)}
            </span>
            <FollowBtn
              isFollow={article?.user_action?.is_follow}
              authorId={article?.media_user?.id}
            />
          </div>
        )}
        <style jsx>
          {`
            .details {
              padding: 20px 0 0;
            }
            .title {
              font-size: 32px;
              font-weight: 500;
              line-height: 48px;
            }
            .info {
              font-size: 14px;
              margin-bottom: 40px;
              color: #999;
              margin-top: 16px;
            }
            .avatar {
              width: 24px;
              height: 24px;
              margin: 0 1px 1px 0;
              border: solid 0.6px #ffffff;
            }
            .source {
              font-size: 14px;
              color: #1f2129;
              margin-right: 24px;
            }
            .time {
              font-size: 14px;
              margin-right: 24px;
              color: #606370;
            }
            .follow-btn {
              width: 60px;
              height: 36px;
              padding: 8px 16px;
              border-radius: 4px;
              background-color: #ffcc32;
              font-size: 14px;
              font-weight: 500;
              color: #1f2129;
            }
          `}
        </style>
      </div>
    )
  }

  function renderBottom() {
    return (
      <div className="article-bottom">
        <div>
          <div className="stats-wrap">
            <div className="read-count font-RPP4MYtWOvsBmV80 tw-text-14 tw-leading-24 tw-mr-25">
              {article?.watch_count_en}人阅读
            </div>
            <InteractVideo
              digg_count={digg_count}
              comment_count={article?.comment_count}
              is_collected={collect_curStatus}
              is_digg={digg_curStatus}
              moreContent={
                <div className="tw-px-24 tw-py-12">
                  <ReportButton
                    objectType={5}
                    objectId={article?.gid}
                    authorId={article?.media_user?.id}
                    articleType={article?.article_type}
                    onClick={() => {
                      sendTeaCommEvent('rt_report', {
                        group_id: article?.gid,
                        author_id: String(article?.media_user?.id),
                        hudong_position: '0',
                        content_type:
                          ARTICLE_CONTENT_TYPE[article?.article_type!]?.value,
                        operation_type: '0',
                      })
                    }}
                    onCancel={() => {
                      sendTeaCommEvent('rt_report', {
                        group_id: article?.gid,
                        author_id: String(article?.media_user?.id),
                        hudong_position: '0',
                        content_type:
                          ARTICLE_CONTENT_TYPE[article?.article_type!]?.value,
                        operation_type: '0',
                      })
                    }}
                  />
                </div>
              }
              shareContent={({ close }) => (
                <Share
                  url={`https://www.dongchedi.com/article/${article?.gid}?zt=pc_redirect`}
                  title={article?.title}
                  onCopySuccess={close}
                  onShare={(text) => {
                    sendTeaCommEvent('clk_event', {
                      event_id: 'ugc_fenxiang',
                      author_id: String(article?.media_user?.id),
                      hudong_position: '0',
                      group_id: article?.gid,
                      obj_text: text,
                    })
                  }}
                />
              )}
              onClickComment={() => {
                sendTeaCommEvent('rt_reply_comment', {
                  author_id: String(article?.media_user?.id),
                  hudong_position: '0',
                  group_id: article?.gid,
                  content_type:
                    ARTICLE_CONTENT_TYPE[article?.article_type!]?.value,
                })
              }}
              onClickDigg={(curStatus) => {
                diggUpdate()
                sendTeaCommEvent(curStatus ? 'rt_unlike' : 'rt_like', {
                  group_id: article?.gid,
                  author_id: String(article?.media_user?.id),
                  hudong_position: '0',
                  content_type:
                    ARTICLE_CONTENT_TYPE[article?.article_type!]?.value,
                })
              }}
              onClickCollect={(curStatus) => {
                collectUpdate()
                sendTeaCommEvent(
                  curStatus ? 'rt_not_favourite' : 'rt_favourite',
                  {
                    group_id: article?.gid,
                    author_id: String(article?.media_user?.id),
                    hudong_position: '0',
                    content_type:
                      ARTICLE_CONTENT_TYPE[article?.article_type!]?.value,
                  }
                )
              }}
            />
          </div>
        </div>
        <style jsx>
          {`
            .article-bottom {
              overflow: hidden;
            }
            .score-wrap {
              float: left;
              line-height: 48px;
              font-size: 34px;
              font-weight: 500;
            }
            .stats-wrap {
              overflow: hidden;
              display: flex;
              justify-content: flex-end;
              align-items: center;
            }
            .fans-count {
              font-size: 14px;
              color: #606370;
            }
            .tt {
              font-size: 16px;
              color: #1f2129;
            }
            .score-stats {
              font-size: 14px;
              color: #606370;
            }

            .read-count {
              color: #1f2129;
              display: inline-block;
            }
            .action-group {
              outline: none;
              font-size: 16px;
              color: #1f2129;
              cursor: default;
            }
          `}
        </style>
      </div>
    )
  }

  /* 渲染文章信息 */
  function renderContent(
    type: 'mix' | 'article',
    clue: Record<'zt' | 'ls', string>
  ) {
    const imageList = get(article, 'gallery_info.image_list', [])
    if (!article.content && imageList?.length) {
      return (
        <div className="image-list">
          {imageList.map((img: any) => {
            return (
              <div key={img.image_uri} className="item">
                <img src={`//p3.dcarimg.com/large/${img.image_uri}`} />
                <p>{img.desc}</p>
              </div>
            )
          })}
          <style jsx>
            {`
              .image-list {
                padding: 0 0 60px;
              }
              p {
                line-height: 1.75;
                margin-top: 20px;
                font-size: 16px;
              }
              img {
                width: 100%;
                margin-top: 20px;
              }
            `}
          </style>
        </div>
      )
    }
    if (type === 'mix') {
      const newContent = article.content
        ?.replace(/{!--\s*PGC_VIDEO:(.*)--}/, '')
        .split('<p></p>')
      return (
        <div
          className="details"
          ref={(node) => {
            if (!node) {
              return
            }
            if (!canUseWebp) {
              const imgEles = node.querySelectorAll('img')
              for (const imgELe of imgEles) {
                imgELe.src = imgELe.src.replace(/\.webp$/, '.png')
              }
            }
          }}
        >
          {newContent.map((section, index) => {
            return (
              <React.Fragment key={index}>
                <section
                  id="article"
                  className="article-content"
                  dangerouslySetInnerHTML={{
                    __html: section
                      ? handleContent(
                          xss.filterXSS(section, {
                            onTagAttr: (tag, name, value) => {
                              if (tag === 'a' && name === 'data-concern-id')
                                return 'class=ttTopic'
                            },
                          }),
                          article.motor_material_car_info,
                          clue
                        )
                      : '',
                  }}
                />
                {index === 0 ? (
                  <section className="article-detail-video-wrap">
                    <Video
                      vid={article?.video_info?.vid}
                      group_id={group_id}
                      fluid
                    />
                  </section>
                ) : null}
              </React.Fragment>
            )
          })}
          <style jsx>
            {`
              section {
                width: 84.44%;
                min-width: 556px;
              }
              .details {
                padding: 0 0 40px;
              }
              :global(.article-detail-video-wrap) {
                margin-right: auto;
                margin-left: auto;
              }
            `}
          </style>
        </div>
      )
    }
    if (type === 'article') {
      return (
        <div
          className="details"
          ref={(node) => {
            if (!node) {
              return
            }
            if (!canUseWebp) {
              const imgEles = node.querySelectorAll('img')
              for (const imgELe of imgEles) {
                imgELe.src = imgELe.src.replace(/\.webp$/, '.png')
              }
            }
          }}
        >
          <section
            id="article"
            className="article-content"
            dangerouslySetInnerHTML={{
              __html: article.content
                ? handleContent(
                    xss.filterXSS(
                      replaceOuterHTML(article.content).replace(
                        /<img src="https:\/\/p1-dcd\.byteimg\.com\/img\/pgc-image\/7d09598eda9a48ff9eacc77ef2bf23b2~noop/g,
                        '<img class="QR-img" src="https://p1-dcd.byteimg.com/img/pgc-image/7d09598eda9a48ff9eacc77ef2bf23b2~noop'
                      ),
                      {
                        onTagAttr: (tag, name, value) => {
                          if (tag === 'a' && name === 'data-concern-id')
                            return 'class=ttTopic'
                        },
                      }
                    ),
                    article.motor_material_car_info,
                    clue
                  )
                : `
            <div style="width: 100%; text-align: center;">
              <img src="${require('static/error-pic@3x.jpg')}" style="width: 240px;"/>
              文章消失了，再去看看别的吧～
            </div>
          `,
            }}
          />
          <style jsx>
            {`
              .details {
                padding: 0 0 40px;
              }
            `}
          </style>
        </div>
      )
    }
  }

  function renderArticle(
    isVideo: boolean,
    content: string,
    clue: Record<'zt' | 'ls', string>
  ) {
    if (isVideo) {
      return renderContent('mix', clue)
    } else {
      return renderContent('article', clue)
    }
  }

  const articleRef = React.useRef<HTMLDivElement>(null)
  addRelationSeriesListeners(articleRef, (event, type, params) => {
    relationSeriesEvent(type, params)
  })
  return (
    <article>
      {renderHeader()}
      <div ref={articleRef}>
        {renderArticle(isVideo, article.content, currentClue)}
      </div>
      {renderBottom()}
      <style jsx>
        {`
          :global(.article-content p, .article-content h1) {
            line-height: 1.75;
            margin-top: 20px;
            font-size: 16px;
          }
          :global(.article-content img) {
            width: 84.44%;
            min-width: 556px;
            height: 100%;
            margin: 20px auto;
            display: block;
            border-radius: 2px;
          }
          :global(.article-content img.QR-img) {
            width: auto;
            min-width: 0;
            height: auto;
            border-radius: 2px;
          }
          :global(.article-content ul, .article-content ol) {
            margin: 1em;
            padding: 0 1em;
          }
          :global(.article-content ul ul) {
            margin: 0 1em;
          }
          :global(.article-content ul li) {
            list-style: circle;
          }
          :global(.article-content ol li) {
            list-style: decimal;
          }
          :global(.article-content h2) {
            margin: 20px 0;
          }
          :global(.article-content li) {
            font-size: 16px;
            line-height: 1.75;
          }
          :global(.article-content a) {
            color: #205be6;
          }
          /* 置灰文章详情页中，头条不可点击的话题a标签颜色 */
          :global(.article-content a.ttTopic) {
            color: #1a1a1a;
            cursor: text;
          }
        `}
      </style>
    </article>
  )
}
